/**
 * ShortcodeDialog — corp's "Insert Shortcode" dialog (corp rich-text-editor):
 * the shortcodes by category, each category collapsible, each shortcode a
 * button showing its code and description. Picking one inserts it and closes.
 */
import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { listShortcodes, type ShortcodeGroup } from '@/services/shortcodes.service';

export function ShortcodeDialog({ onInsert, onClose }: { onInsert: (code: string) => void; onClose: () => void }) {
  const [groups, setGroups] = useState<ShortcodeGroup[] | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    listShortcodes()
      .then((g) => {
        if (cancelled) return;
        setGroups(g);
        // The first category starts open.
        if (g[0]) setOpen(new Set([g[0].category]));
      })
      .catch(() => {
        if (!cancelled) setGroups([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function toggle(category: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="xl"
      title={<h2 className="text-lg font-semibold leading-none text-slate-900">Insert Shortcode</h2>}
      footer={
        <div className="flex w-full justify-end">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {groups === null ? (
        <p className="py-8 text-center text-sm text-slate-500">Loading shortcodes...</p>
      ) : groups.length === 0 ? (
        <p className="py-8 text-center text-sm text-slate-500">No shortcodes available</p>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.category} className="space-y-2">
              <button
                type="button"
                aria-expanded={open.has(g.category)}
                onClick={() => toggle(g.category)}
                className="flex w-full items-center justify-between rounded bg-slate-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-700 transition-colors hover:bg-slate-200"
              >
                <span>{g.category}</span>
                {open.has(g.category) ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
              </button>
              {open.has(g.category) && (
                <div className="grid gap-1">
                  {g.items.map((sc) => (
                    <button
                      key={sc.code}
                      type="button"
                      onClick={() => onInsert(sc.code)}
                      className="w-full rounded-md border border-transparent px-4 py-3 text-left transition-colors hover:border-blue-200 hover:bg-blue-50"
                    >
                      <span className="flex items-center gap-2">
                        <code className="rounded bg-blue-100 px-2 py-1 font-mono text-sm text-blue-700">{sc.code}</code>
                        <span className="text-sm font-medium text-slate-900">{sc.description}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
